import { useNavigate, useSearch } from '@tanstack/react-router'
import { message } from 'antd'
import { RautReportIndication } from 'features/indicationTriage'
import { getRequiredSections, Report } from 'features/reporting/types'
import { RautFormDataState } from 'pages/reporting/reports/raut/rautReportData'
import { getCompletionStatus } from 'pages/reporting/shared/utils/completionStatus'
import { useCallback, useMemo } from 'react'
import { GeckoInspection, GeckoVisualInspection } from 'types'

interface UseReportNavigationResult {
  activePage: string | null
  navigateToPage: (page: string) => void
  returnToMenu: () => void
  canNavigate: (page: string) => boolean
  getPageTitle: (page: string) => string
  completionStatus: Record<
    string,
    {
      isComplete: boolean
      completedFields: number
      totalFields: number
    }
  >
}

interface UseReportNavigationProps {
  formData: RautFormDataState
  report: Report
  indicationImages: { totalIndications: number; capturedScreenshots: number }
  sortedIndications: RautReportIndication[]
  inspections: readonly (GeckoInspection | GeckoVisualInspection)[]
  headInspectionIds?: ReadonlySet<string>
}

/**
 * Hook to manage report navigation and page completion status
 * Navigation is synced with URL search params for shareable links
 */
export const useReportNavigation = ({
  formData,
  report,
  indicationImages,
  sortedIndications,
  inspections,
  headInspectionIds,
}: UseReportNavigationProps): UseReportNavigationResult => {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as {
    section?: string
    templateId?: string
    metadataEntryId?: string
  }

  // Get active page from URL search params
  const activePage = searchParams.section || null

  // Calculate completion status for all sections
  const completionStatus = useMemo(
    () =>
      getCompletionStatus(
        {
          ...formData,
          indicationImages,
          sortedIndications,
          savedIndicationGraphs: formData.savedIndicationGraphs,
          headInspectionIds,
        },
        inspections
      ),
    [
      formData,
      indicationImages,
      sortedIndications,
      inspections,
      headInspectionIds,
    ]
  )

  /**
   * Check if navigation to a page is allowed based on dependencies
   */
  const canNavigate = useCallback(
    (page: string): boolean => {
      // Add any navigation rules here
      // For example, requiring cover page completion before other sections
      if (page === 'review') {
        const requiredSections: string[] = getRequiredSections(report)
        const hasIncomplete = requiredSections.some(
          (section) => !completionStatus[section].isComplete
        )

        if (hasIncomplete) {
          message.warning('Please complete all required sections before review')
          // Removing this for development purposes
          // return false
        }
      }

      return true
    },
    [completionStatus, report]
  )

  /**
   * Navigate to a specific page by updating URL search params
   */
  const navigateToPage = useCallback(
    (page: string) => {
      if (canNavigate(page)) {
        void navigate({
          search: ((prev: Record<string, unknown>) => ({
            ...prev,
            section: page,
          })) as Parameters<typeof navigate>[0]['search'],
          replace: false,
        })
      }
    },
    [canNavigate, navigate]
  )

  /**
   * Return to the main menu by removing section from URL
   */
  const returnToMenu = useCallback(() => {
    void navigate({
      search: ((prev: Record<string, unknown>) => {
        const { section, ...rest } = prev as { section?: string } & Record<
          string,
          unknown
        >
        void section
        return rest
      }) as Parameters<typeof navigate>[0]['search'],
      replace: false,
    })
  }, [navigate])

  /**
   * Get the title for a specific page
   */
  const getPageTitle = useCallback((page: string): string => {
    const titles: Record<string, string> = {
      cover: 'Cover Page',
      summary: 'Summary',
      calibration: 'Calibration',
      indications: 'Indications',
      clientImages: 'Client Images',
      assetDrawing: 'Asset Drawings',
      review: 'Review & Sign Off',
      generate: 'Generate PDF',
    }

    return titles[page]
  }, [])

  return {
    activePage,
    navigateToPage,
    returnToMenu,
    canNavigate,
    getPageTitle,
    completionStatus,
  }
}
